/**
 * Compresse une image base64 pour économiser l'espace de stockage
 * @param base64Image - Image en format base64
 * @param maxWidth - Largeur maximale (défaut: 3840 - 4K pour permettre le zoom)
 * @param maxHeight - Hauteur maximale (défaut: 2160 - 4K pour permettre le zoom)
 * @param quality - Qualité JPEG (défaut: 0.92 - haute qualité)
 * @returns Promise<string> - Image compressée en base64
 */
export async function compressImage(
  base64Image: string,
  maxWidth: number = 3840,
  maxHeight: number = 2160,
  quality: number = 0.92
): Promise<string> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    
    img.onload = () => {
      // Créer un canvas pour la compression
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      
      if (!ctx) {
        // Si la compression échoue, retourner l'image originale
        resolve(base64Image);
        return;
      }
      
      // Calculer les nouvelles dimensions en gardant le ratio
      let width = img.width;
      let height = img.height;
      
      if (width > maxWidth || height > maxHeight) {
        const ratio = Math.min(maxWidth / width, maxHeight / height);
        width = width * ratio;
        height = height * ratio;
      }
      
      canvas.width = width;
      canvas.height = height;
      
      // Dessiner l'image compressée
      ctx.drawImage(img, 0, 0, width, height);
      
      // Convertir en base64 avec compression JPEG
      const compressedImage = canvas.toDataURL('image/jpeg', quality);
      
      resolve(compressedImage);
    };
    
    img.onerror = () => {
      // Si le chargement échoue, retourner l'image originale
      resolve(base64Image);
    };
    
    img.src = base64Image;
  });
}

/**
 * Vérifie si une image doit être compressée
 * @param base64Image - Image en format base64
 * @returns boolean - True si l'image doit être compressée
 */
export function shouldCompressImage(base64Image: string): boolean {
  // Si l'image fait plus de 800KB, elle devrait être compressée
  const sizeInBytes = base64Image.length;
  const sizeInKB = sizeInBytes / 1024;
  
  // Vérifier aussi si c'est déjà un JPEG (commence par data:image/jpeg)
  const isJpeg = base64Image.startsWith('data:image/jpeg');
  
  // Compresser si > 800KB ou si ce n'est pas déjà un JPEG optimisé
  return sizeInKB > 800 || !isJpeg;
}